import { ImageWithMetadata, isImageWithMetadata } from '@jump-tech-frontend/domain';

export interface FileResource {
  getValue(): string;
  setValue(value: string): void;
  toJson(): any;
  getAnalysisId?(): string | undefined;
}

export const makeFileResource = (src: any): FileResource | null => {
  if (!src) {
    return null;
  }

  // Check for ImageWithMetadata first (has imageData and optional analysisId)
  if (isImageWithMetadata(src)) {
    return ImageWithMetadataResource.from(src);
  }

  if (typeof src === 'string') {
    return ImageResource.from(src);
  } else if (typeof src === 'object') {
    // Check for attachment (has name and data)
    return AttachmentResource.from(src);
  }

  return null;
};

/**
 * An image resource is a simple string.
 */
export class ImageResource implements FileResource {
  constructor(private value: string) {}

  static from(value: string): ImageResource {
    return new ImageResource(value);
  }

  getValue(): string {
    return this.value;
  }

  setValue(value: string) {
    this.value = value;
  }

  toJson(): string {
    return this.value;
  }

  getAnalysisId(): string | undefined {
    return undefined;
  }
}

/**
 * An attachment resource is an object with a name and a data field.
 */
export class AttachmentResource implements FileResource {
  constructor(private name: string, private data: string) {}

  static from(src: any): AttachmentResource | null {
    if (src === null || !('name' in src) || !('data' in src)) {
      return null;
    }
    return new AttachmentResource(src.name, src.data);
  }

  getValue(): string {
    return this.data;
  }

  setValue(value: string) {
    this.data = value;
  }

  getName(): string {
    return this.name;
  }

  toJson(): any {
    return {
      name: this.name,
      data: this.data
    };
  }

  getAnalysisId(): string | undefined {
    return undefined;
  }
}

/**
 * An image with the id of its analysis
 * Used for AI-validated images that need to keep their analysis reference through S3 upload
 */
export class ImageWithMetadataResource implements FileResource {
  constructor(
    private imageData: string,
    private analysisId?: string
  ) {}

  static from(src: ImageWithMetadata): ImageWithMetadataResource {
    return new ImageWithMetadataResource(src.imageData, src.analysisId);
  }

  getValue(): string {
    return this.imageData;
  }

  setValue(value: string) {
    this.imageData = value;
  }

  getAnalysisId(): string | undefined {
    return this.analysisId;
  }

  toJson(): ImageWithMetadata {
    return {
      imageData: this.imageData,
      analysisId: this.analysisId
    };
  }
}
